import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArmchair,
  IconEdit,
  IconLayout2,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useBranches } from '../branches/branchesApi';
import {
  useDeleteTable,
  useDeleteZone,
  useTables,
  useZones,
} from './floorPlanApi';
import { ZoneFormDrawer } from './ZoneFormDrawer';
import { TableFormDrawer } from './TableFormDrawer';
import type { RestaurantTableDto, RestaurantZoneDto } from './types';

// Combined Zones + Tables page. Zones tab manages the floor's areas;
// Tables tab manages individual seats inside the selected zone. Per-
// branch picker drives both — switching branches reloads both lists.

export function FloorPlanPage() {
  const { t, i18n } = useTranslation();
  const branchesQuery = useBranches();
  const branches = useMemo(() => branchesQuery.data ?? [], [branchesQuery.data]);

  const [branchId, setBranchId] = useState<string | null>(null);
  const effectiveBranchId = branchId ?? branches[0]?.branchId ?? null;

  const zonesQuery = useZones(effectiveBranchId);
  const tablesQuery = useTables(effectiveBranchId);
  const deleteZone = useDeleteZone(effectiveBranchId);
  const deleteTable = useDeleteTable(effectiveBranchId);

  const zones = zonesQuery.data ?? [];
  const tables = tablesQuery.data ?? [];

  // Tab is the active zone id, controlling which tables appear and
  // which zone new tables default into.
  const [activeZoneId, setActiveZoneId] = useState<string | null>(null);
  const effectiveZoneId = activeZoneId ?? zones[0]?.restaurantZoneId ?? null;

  const [zoneDrawerOpen, setZoneDrawerOpen] = useState(false);
  const [zoneEditing, setZoneEditing] = useState<RestaurantZoneDto | null>(null);
  const [zoneDeleting, setZoneDeleting] = useState<RestaurantZoneDto | null>(null);

  const [tableDrawerOpen, setTableDrawerOpen] = useState(false);
  const [tableEditing, setTableEditing] = useState<RestaurantTableDto | null>(null);
  const [tableDeleting, setTableDeleting] = useState<RestaurantTableDto | null>(null);

  const branchOptions = branches.map((b) => ({
    value: b.branchId,
    label: i18n.language === 'ar' ? b.nameAr : (b.nameEn ?? b.nameAr),
  }));

  const tablesInActiveZone = useMemo(
    () =>
      effectiveZoneId
        ? tables.filter((tbl) => tbl.restaurantZoneId === effectiveZoneId)
        : [],
    [tables, effectiveZoneId],
  );

  const confirmZoneDelete = async () => {
    if (!zoneDeleting) return;
    try {
      await deleteZone.mutateAsync(zoneDeleting.restaurantZoneId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: zoneDeleting.nameAr,
      });
      setZoneDeleting(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  const confirmTableDelete = async () => {
    if (!tableDeleting) return;
    try {
      await deleteTable.mutateAsync(tableDeleting.restaurantTableId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: tableDeleting.nameAr,
      });
      setTableDeleting(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.operations')}
        title={t('floorPlan.page.title')}
        subtitle={t('floorPlan.page.subtitle')}
      />

      <Paper p="md">
        <Select
          label={t('floorPlan.page.branchLabel')}
          data={branchOptions}
          value={effectiveBranchId}
          onChange={(v) => {
            setBranchId(v);
            setActiveZoneId(null);
          }}
          searchable
          disabled={branchesQuery.isLoading || branches.length === 0}
          maw={360}
        />
      </Paper>

      {!effectiveBranchId ? (
        <Paper p="lg">
          <EmptyState
            icon={<IconLayout2 size={22} stroke={1.6} />}
            title={t('floorPlan.empty.noBranchesTitle')}
            description={t('floorPlan.empty.noBranchesBody')}
          />
        </Paper>
      ) : zonesQuery.isLoading || tablesQuery.isLoading ? (
        <Paper p="lg">
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        </Paper>
      ) : (
        <Stack gap="lg">
          {/* Zones panel */}
          <Paper p={zones.length === 0 ? 'lg' : 'md'}>
            <Group justify="space-between" mb={zones.length === 0 ? 0 : 'sm'}>
              <div>
                <Text fw={500} c="var(--wheels-ink-strong)">
                  {t('floorPlan.zones.sectionTitle')}
                </Text>
                <Text fz="xs" c="var(--wheels-ink-muted)">
                  {t('floorPlan.zones.sectionSubtitle')}
                </Text>
              </div>
              <Button
                size="xs"
                leftSection={<IconPlus size={14} stroke={1.8} />}
                onClick={() => {
                  setZoneEditing(null);
                  setZoneDrawerOpen(true);
                }}
              >
                {t('floorPlan.zones.create')}
              </Button>
            </Group>

            {zones.length === 0 ? (
              <EmptyState
                icon={<IconLayout2 size={22} stroke={1.6} />}
                title={t('floorPlan.empty.zonesTitle')}
                description={t('floorPlan.empty.zonesBody')}
              />
            ) : (
              <Table verticalSpacing="sm" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('floorPlan.zones.columns.code')}</Table.Th>
                    <Table.Th>{t('floorPlan.zones.columns.nameAr')}</Table.Th>
                    <Table.Th>{t('floorPlan.zones.columns.nameEn')}</Table.Th>
                    <Table.Th>{t('floorPlan.zones.columns.status')}</Table.Th>
                    <Table.Th style={{ width: 100, textAlign: 'end' }}>
                      {t('common.actions')}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {zones.map((z) => (
                    <Table.Tr key={z.restaurantZoneId}>
                      <Table.Td>
                        <span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">
                          {z.code}
                        </span>
                      </Table.Td>
                      <Table.Td fw={500}>{z.nameAr}</Table.Td>
                      <Table.Td c="var(--wheels-ink-muted)">{z.nameEn ?? '—'}</Table.Td>
                      <Table.Td>
                        {z.isActive ? (
                          <Badge color="green" variant="light" radius="sm">
                            {t('common.active')}
                          </Badge>
                        ) : (
                          <Badge color="gray" variant="light" radius="sm">
                            {t('common.inactive')}
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" justify="flex-end" wrap="nowrap">
                          <Tooltip label={t('common.edit')} withinPortal>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={() => {
                                setZoneEditing(z);
                                setZoneDrawerOpen(true);
                              }}
                              aria-label={t('common.edit')}
                            >
                              <IconEdit size={16} stroke={1.6} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('common.delete')} withinPortal>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => setZoneDeleting(z)}
                              aria-label={t('common.delete')}
                            >
                              <IconTrash size={16} stroke={1.6} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>

          {/* Tables panel — needs at least one zone */}
          {zones.length > 0 && (
            <Paper p="md">
              <Group justify="space-between" mb="sm">
                <div>
                  <Text fw={500} c="var(--wheels-ink-strong)">
                    {t('floorPlan.tables.sectionTitle')}
                  </Text>
                  <Text fz="xs" c="var(--wheels-ink-muted)">
                    {t('floorPlan.tables.sectionSubtitle')}
                  </Text>
                </div>
                <Button
                  size="xs"
                  leftSection={<IconPlus size={14} stroke={1.8} />}
                  onClick={() => {
                    setTableEditing(null);
                    setTableDrawerOpen(true);
                  }}
                >
                  {t('floorPlan.tables.create')}
                </Button>
              </Group>

              <Tabs
                value={effectiveZoneId}
                onChange={(v) => setActiveZoneId(v)}
                variant="outline"
              >
                <Tabs.List>
                  {zones.map((z) => (
                    <Tabs.Tab key={z.restaurantZoneId} value={z.restaurantZoneId}>
                      {i18n.language === 'ar' ? z.nameAr : (z.nameEn ?? z.nameAr)}
                    </Tabs.Tab>
                  ))}
                </Tabs.List>

                {zones.map((z) => (
                  <Tabs.Panel key={z.restaurantZoneId} value={z.restaurantZoneId} pt="md">
                    {effectiveZoneId === z.restaurantZoneId &&
                      (tablesInActiveZone.length === 0 ? (
                        <EmptyState
                          icon={<IconArmchair size={22} stroke={1.6} />}
                          title={t('floorPlan.empty.tablesTitle')}
                          description={t('floorPlan.empty.tablesBody')}
                          actionLabel={t('floorPlan.tables.create')}
                          onAction={() => {
                            setTableEditing(null);
                            setTableDrawerOpen(true);
                          }}
                        />
                      ) : (
                        <Table verticalSpacing="sm" highlightOnHover striped="even">
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>{t('floorPlan.tables.columns.code')}</Table.Th>
                              <Table.Th>{t('floorPlan.tables.columns.name')}</Table.Th>
                              <Table.Th>{t('floorPlan.tables.columns.capacity')}</Table.Th>
                              <Table.Th>{t('floorPlan.tables.columns.status')}</Table.Th>
                              <Table.Th style={{ width: 100, textAlign: 'end' }}>
                                {t('common.actions')}
                              </Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {tablesInActiveZone.map((tbl) => (
                              <Table.Tr key={tbl.restaurantTableId}>
                                <Table.Td>
                                  <span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">
                                    {tbl.code}
                                  </span>
                                </Table.Td>
                                <Table.Td fw={500}>{tbl.nameAr}</Table.Td>
                                <Table.Td className="font-numeric">{tbl.capacity}</Table.Td>
                                <Table.Td>
                                  {tbl.isActive ? (
                                    <Badge color="green" variant="light" radius="sm">
                                      {t('common.active')}
                                    </Badge>
                                  ) : (
                                    <Badge color="gray" variant="light" radius="sm">
                                      {t('common.inactive')}
                                    </Badge>
                                  )}
                                </Table.Td>
                                <Table.Td>
                                  <Group gap="xs" justify="flex-end" wrap="nowrap">
                                    <Tooltip label={t('common.edit')} withinPortal>
                                      <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        onClick={() => {
                                          setTableEditing(tbl);
                                          setTableDrawerOpen(true);
                                        }}
                                        aria-label={t('common.edit')}
                                      >
                                        <IconEdit size={16} stroke={1.6} />
                                      </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label={t('common.delete')} withinPortal>
                                      <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        onClick={() => setTableDeleting(tbl)}
                                        aria-label={t('common.delete')}
                                      >
                                        <IconTrash size={16} stroke={1.6} />
                                      </ActionIcon>
                                    </Tooltip>
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      ))}
                  </Tabs.Panel>
                ))}
              </Tabs>
            </Paper>
          )}
        </Stack>
      )}

      {effectiveBranchId && (
        <ZoneFormDrawer
          opened={zoneDrawerOpen}
          onClose={() => setZoneDrawerOpen(false)}
          branchId={effectiveBranchId}
          zone={zoneEditing}
        />
      )}

      {effectiveBranchId && zones.length > 0 && (
        <TableFormDrawer
          opened={tableDrawerOpen}
          onClose={() => setTableDrawerOpen(false)}
          branchId={effectiveBranchId}
          defaultZoneId={effectiveZoneId}
          zones={zones}
          table={tableEditing}
        />
      )}

      <ConfirmDialog
        opened={zoneDeleting !== null}
        onClose={() => setZoneDeleting(null)}
        onConfirm={confirmZoneDelete}
        title={t('floorPlan.zones.deleteTitle')}
        message={t('floorPlan.zones.deleteBody', { name: zoneDeleting?.nameAr ?? '' })}
        loading={deleteZone.isPending}
      />

      <ConfirmDialog
        opened={tableDeleting !== null}
        onClose={() => setTableDeleting(null)}
        onConfirm={confirmTableDelete}
        title={t('floorPlan.tables.deleteTitle')}
        message={t('floorPlan.tables.deleteBody', { name: tableDeleting?.nameAr ?? '' })}
        loading={deleteTable.isPending}
      />
    </Stack>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
